// FP 간트 — 표시 배율 컨트롤(버킷 / 가로 폭 / 행 높이). 주문·자원 간트 공용.
//
// 두 화면에 같은 마크업이 복제돼 있던 것을 여기로 합쳤다(툴팁을 `GanttTooltip.js`로 합친 것과 같은
// 이유 — 한쪽만 고치면 스타일이 갈라진다). 설계 근거는 reports/gantt-implementation.md §7-5,
// 시안은 docs/fp-frontend-migration/prototypes/gantt-toolbar-buttons.html.
//
// 기능 구성은 old(`HeaderUnitSetting` + CommonIconButton 4개)와 같고 **배치·아이콘·상태 표시만** 바꿨다:
//   ① 가로/세로를 **같은 아이콘 가족의 회전**으로 구분한다 — `UnfoldMore/Less`를 가로만 90° 돌린다.
//      옛 구성은 가로 = 돋보기, 세로 = ↕ 로 계열이 달라 툴팁 없이는 어느 버튼이 어느 축인지 알 수 없었다.
//      (old는 반대로 돋보기 = 행 높이였는데, 돋보기는 양축 확대로 읽혀 직관에 어긋난다.)
//   ② **구분선으로 시간축 / 행 높이 두 그룹**을 나눈다 — 5개가 일렬이면 한 덩어리로 보인다.
//   ③ **네 버튼 모두 한계에서 disabled**. 이전에는 행 높이만 걸려 있어 "한 버튼만 회색"인 상태가
//      고장처럼 보였고, 폭 버튼은 한계에서도 눌리며 안에서 조용히 clamp 됐다.
//   ④ 버킷은 **현재 값이 보이는 Select**를 유지한다(old의 달력 아이콘 → Menu는 현재 단위를 감춘다).
//      단 라벨 없는 30px로 낮춰 아이콘 줄과 높이를 맞춘다.
//
// 아이콘 색은 `info` — 같은 줄 맨 끝의 필터 접기 버튼이 `FilterContainer`에서 `color="info"`로
// 고정돼 있어(공용 컴포넌트, 수정 대상 아님) 회색으로 두면 한 줄에 두 계열이 섞인다.
//
// ⑤ **묶음 전체를 하나의 면(pill)으로 감싼다**(2026-08-24, §7-15 / 시안 C안
//    `prototypes/gantt-toolbar-grouping.html`). 위 ②의 구분선은 묶음 **안**을 나누는 것이라
//    "묶음이 어디서 끝나는가"를 말해주지 못했고, 그 끝 바로 뒤에 `FilterContainer`가 붙이는
//    **조회조건 접기 버튼**이 같은 색·크기·간격으로 놓여 다섯 번째 간트 버튼처럼 읽혔다.
//    접기 버튼은 우리가 렌더하지 않아 손댈 수 없으므로(공용 컴포넌트) **우리 쪽에서 경계를 만드는
//    것이 유일한 수단**이다 — 여백·구분선은 끝을 암시할 뿐이고, 면은 포함/제외를 형태로 확정한다.
//    색 계열로 나누는 안은 버렸다: 이 버튼들은 배율 한계에서 **실제로 회색 disabled**가 되므로
//    중립색을 정상 상태에 쓰면 정상과 비활성이 같은 색이 된다.

import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Tooltip from "@mui/material/Tooltip";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";

import {
  CTRL_GROUP_SX,
  CTRL_SELECT_SX,
  HEADER_UNITS,
  ROW_HEIGHT_MAX,
  ROW_HEIGHT_MIN,
  ROW_HEIGHT_STEP,
  WIDTH_ZOOM_MAX,
  WIDTH_ZOOM_MIN,
  WIDTH_ZOOM_STEP
} from "@plannel/pages/factory-plan/common/gantt/ganttConstants";

// 부동소수 반복 곱셈이라 경계에서 `>=`가 아슬아슬하게 빗나갈 수 있다(예: 4.000000000000001).
// 상대 허용오차로 비교해 마지막 한 스텝에서 버튼이 안 죽는 일을 막는다.
const atLimit = (value, limit) => Math.abs(value - limit) / limit < 1e-9;

// 버튼 하나 — `Tooltip`은 disabled 자식의 이벤트를 못 받으므로 `span`으로 감싼다(MUI 관례).
const ZoomButton = ({ title, disabled, onClick, icon: Icon, rotate }) => (
  <Tooltip title={title}>
    <Box component="span">
      <IconButton color="info" size="small" disabled={disabled} onClick={onClick}>
        <Icon fontSize="small" sx={rotate ? { transform: `rotate(${rotate}deg)` } : null} />
      </IconButton>
    </Box>
  </Tooltip>
);

/**
 * headerUnit / widthZoom / rowHeight 는 화면이 소유하고, 여기서는 클램프까지만 책임진다.
 * onXxxChange 는 값을 그대로 받는다(setter 시그니처와 맞춘다).
 */
const GanttViewControls = ({
  t,
  // 이 묶음 **맨 앞**에 끼워 넣을 컨트롤(자원 간트의 작업 검색 pill). `FilterContainer`는 마지막
  // 자식만 우측 정렬하므로, 우측 묶음에 들어가려면 그 자식 **안**에 있어야 한다 — 형제로 두면
  // 조회조건들과 같이 왼쪽에 붙는다(2026-08-21 확인).
  startSlot,
  headerUnit, onHeaderUnitChange,
  widthZoom, onWidthZoomChange,
  rowHeight, onRowHeightChange,
  // 면(pill) 스타일은 보정 툴바와 공유한다(`CTRL_GROUP_SX`) — 두 묶음이 한 줄에 나란히 놓인다.
  // 기본 `mr`은 **조회조건 줄에서 쓸 때 접기 버튼과의 간격**이다(면 밖의 여백이라 "이 버튼은 묶음
  // 소속이 아니다"를 한 번 더 말한다). 조회조건 줄이 아닌 곳(보정 간트의 툴바 줄)에서는 접기 버튼이
  // 없어 그 여백이 오른쪽 끝에 뜨는 틈이 되므로 `sx={{ mr: 0 }}`로 끈다.
  sx
}) => (
  <Box sx={{ ...CTRL_GROUP_SX, mr: 1, ...sx }}>
    {startSlot ? (
      <>
        {startSlot}
        <Divider orientation="vertical" flexItem sx={{ mx: 0.75, my: 0.75 }} />
      </>
    ) : null}

    {/* 버킷(시/일/주/월/가변) — old HeaderUnitSetting. **조회 조건이 아니라 표시 배율**이라
        조회조건 줄이 아니라 우측 표시 버튼 묶음에 둔다. 목록은 DB 조회 대신 정적 카탈로그. */}
    <Select
      size="small"
      value={headerUnit}
      onChange={(event) => onHeaderUnitChange(event.target.value)}
      sx={CTRL_SELECT_SX}
      inputProps={{ "aria-label": t("BUCKET") }}
    >
      {HEADER_UNITS.map(unit => (
        <MenuItem key={unit.value} value={unit.value}>{t(unit.labelKey)}</MenuItem>
      ))}
    </Select>

    <Divider orientation="vertical" flexItem sx={{ mx: 0.75, my: 0.75 }} />

    {/* 시간축(가로 폭) — 같은 아이콘을 90° 돌려 "좌우로 늘린다"를 형태로 말한다. */}
    <ZoomButton
      title={t("FP_WIDTH_ZOOM_IN")}
      icon={UnfoldMoreIcon}
      rotate={90}
      disabled={atLimit(widthZoom, WIDTH_ZOOM_MAX) || widthZoom > WIDTH_ZOOM_MAX}
      onClick={() => onWidthZoomChange(Math.min(WIDTH_ZOOM_MAX, widthZoom * WIDTH_ZOOM_STEP))}
    />
    <ZoomButton
      title={t("FP_WIDTH_ZOOM_OUT")}
      icon={UnfoldLessIcon}
      rotate={90}
      disabled={atLimit(widthZoom, WIDTH_ZOOM_MIN) || widthZoom < WIDTH_ZOOM_MIN}
      onClick={() => onWidthZoomChange(Math.max(WIDTH_ZOOM_MIN, widthZoom / WIDTH_ZOOM_STEP))}
    />

    <Divider orientation="vertical" flexItem sx={{ mx: 0.75, my: 0.75 }} />

    {/* 행 높이 — 회전 없는 세로 방향 그대로. */}
    <ZoomButton
      title={t("FP_ZOOM_IN")}
      icon={UnfoldMoreIcon}
      disabled={rowHeight >= ROW_HEIGHT_MAX}
      onClick={() => onRowHeightChange(Math.min(ROW_HEIGHT_MAX, rowHeight + ROW_HEIGHT_STEP))}
    />
    <ZoomButton
      title={t("FP_ZOOM_OUT")}
      icon={UnfoldLessIcon}
      disabled={rowHeight <= ROW_HEIGHT_MIN}
      onClick={() => onRowHeightChange(Math.max(ROW_HEIGHT_MIN, rowHeight - ROW_HEIGHT_STEP))}
    />

    {/* TODO(MIG-T2): [ordergantt/OrderGantt, resourcegantt/ResourceGantt] "화면에 맞춤"(기본 배율 복귀) 미구현.
          old: 없음(신규 제안). 랭팩 `fitToScreen`("화면에 맞춤")은 이미 있어 키 추가는 불필요.
          사유: 2026-08-21 사용자 지시로 이번 범위에서 제외 — 되돌릴 범위(폭·행 높이만 vs 버킷까지)가
                미정이다. `reports/deferred-features.md` 참고 */}
    {/* TODO(MIG-T2): [ordergantt/OrderGantt, resourcegantt/ResourceGantt] 툴팁 단축키 병기 미구현.
          old: 없음. `Ctrl + 휠`(확대) / `Alt + 드래그`(이동)는 이미 동작하는데 화면에 안내가 없다.
          사유: 해당 문구의 랭팩 키가 없어 한글 하드코딩을 피했다 — 키 신설 여부가 결정되면 붙인다 */}
  </Box>
);

export default GanttViewControls;
